// Desk sidebar (design spec 2026-10-19 §4; UX pass 2026-10-27 1.6, 0.9; SHELL-DECISIONS: Home was
// renamed Desk, the file keeps its name): the person's queue. The Desk row, then "Needs you": Inbox (the unread count; it opens the Inbox page, like the
// rail), Approvals (the caller's pending decisions, with the agents add-on), My numbers (the
// numbers still to enter, when the KPI pages are open to them) and Hours to approve (managers,
// this week's pending hours). Each row is a read Home's page already makes; a row the person
// has no access to is absent and a zero count shows no pill. No footer: Add-ons live at Setup ›
// Add-ons.
import { CountPill, NavList, NavRow, SidebarCaption, SidebarPanel } from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useRouteContext } from "@tanstack/react-router";
import { ClockIcon, GaugeIcon, HandIcon, InboxIcon, LampDeskIcon } from "lucide-react";
import type { ReactNode } from "react";
import { canOpen } from "../../extensions/modes";
import type { SidebarProps } from "../../extensions/registry";
import { myApprovalsCountQuery } from "../../features/agents/agent-queries";
import { isManager } from "../../features/company";
import { outstanding } from "../../features/numbers/my-numbers-model";
import { myNumbersQuery, weekTimeQuery } from "../../features/queries";
import { usePeriod } from "../../features/screen-parts";
import { useEveryPage } from "../../features/use-every-page";
import { useFeature, useSession } from "../../features/use-feature";
import { useInboxUnread } from "../../features/work/inbox-list";
import { keepFocus } from "../../focus/focus";

/**
 * The week's hours waiting for approval, across every page read; 0 (no pill) until the last page
 * is in, so the count is never understated.
 */
export function pendingHours(
  pages: readonly { data: readonly { approval_status: string }[] }[] | undefined,
  more: boolean,
): number {
  if (more || !pages) return 0;
  return pages.flatMap((p) => p.data).filter((r) => r.approval_status === "pending").length;
}

export function HomeSidebar({ company, children }: SidebarProps) {
  const { api } = useRouteContext({ from: "__root__" });
  const { me } = useSession();
  const params = { slug: company.slug };
  const co = company.id;
  const week = usePeriod();
  const agents = useFeature("agents", co).enabled && canOpen("agents", me, company);
  const numbersOpen = canOpen("cadence", me, company, true);
  const manager = isManager(company);

  const unread = useInboxUnread(api, co, true) ?? 0;
  const approvals = useQuery({ ...myApprovalsCountQuery(api, co), enabled: agents });
  const numbers = useQuery({ ...myNumbersQuery(api, co), enabled: numbersOpen, retry: false });
  const time = useInfiniteQuery({ ...weekTimeQuery(api, co, week), enabled: manager });
  // Every page of the week's hours, so the count is exact (shared cache with Home's page).
  useEveryPage(time);
  const approvalsCount = approvals.data?.count ?? 0;
  const due = outstanding(numbers.data ?? undefined).length;
  const hours = pendingHours(time.data?.pages, time.hasNextPage);

  const pill = (n: number, label: string): ReactNode =>
    n > 0 ? <CountPill count={n} tone="accent" aria-label={`${n} ${label}`} /> : null;
  const link = (to: string) => (
    <Link
      to={to as never}
      params={params as never}
      search={keepFocus as never}
      activeOptions={{ includeSearch: false }}
    />
  );

  return (
    <SidebarPanel title="Desk">
      <NavList>
        <NavRow
          icon={LampDeskIcon}
          label="Desk"
          render={
            <Link
              to="/c/$slug/desk"
              params={params}
              search={keepFocus}
              activeOptions={{ exact: true, includeSearch: false }}
            />
          }
        />
      </NavList>
      <SidebarCaption label="Needs you" className="mt-2" />
      <NavList data-slot="home-queue">
        <NavRow
          icon={InboxIcon}
          label="Inbox"
          trailing={pill(unread, "unread")}
          render={<Link to="/c/$slug/inbox" params={params} />}
        />
        {agents ? (
          <NavRow
            icon={HandIcon}
            label="Approvals"
            trailing={pill(approvalsCount, "waiting")}
            render={link("/c/$slug/agents/approvals")}
          />
        ) : null}
        {numbersOpen ? (
          <NavRow
            icon={GaugeIcon}
            label="My numbers"
            trailing={pill(due, "due")}
            render={link("/c/$slug/kpis/my-numbers")}
          />
        ) : null}
        {manager ? (
          <NavRow
            icon={ClockIcon}
            label="Hours to approve"
            trailing={pill(hours, "pending")}
            render={link("/c/$slug/kpis/entry/hours")}
          />
        ) : null}
      </NavList>
      {children}
    </SidebarPanel>
  );
}
